/**
 * Used throughout the app to truncate IDs to the first 6 digits with trailing elipses
 * @param id string
 */
export function truncate(str: string, n: number = 6) {
  return str.length > n ? str.slice(0, n) + "..." : str;
}

/**
 * Function to convert a value to a number
 */
export function toNumber(val: string | number = ""): number {
  if (typeof val === "number") {
    return val;
  }

  if (typeof val !== "string") {
    return NaN; // shouldn't happen, but "any"...
  }

  const trimmedText = val.trim();
  // if a string has leading or ending spaces we have to return false right away,
  // because otherwise Number will return valid for something like: "  123 " or even ""
  if (trimmedText.length === 0 || trimmedText.length < val.length) {
    return NaN;
  }
  return Number(trimmedText);
}

/**
 * Function to check if a string is a number
 * For example these values will return true: "1", "123", "1e24"
 * while these will return false: "1b", "12basdf23", "!3!"
 */
export const isNumber = (val: string | number = "") => !isNaN(toNumber(val));

const REAL_DELIMITER = " ± ";

// Given a string representation of mean and std, build the bigger string
export const toRealString = (
  mean: string | number,
  std: string | number,
): string => `${mean}${REAL_DELIMITER}${std}`;

export const isRealString = (val: string | number) => {
  const parts = String(val).split(REAL_DELIMITER);
  return parts.length === 2 && isNumber(parts[0]) && isNumber(parts[1]);
};

/**
 * Use this function if you need to get the potential size of text in pixels
 * without having to have rendered it first.
 *
 * @param text - the string you want measured
 * @param font - has to be in this format: "18px Roboto", which is font size then font family
 * returns object with type: {width: number, height: number}
 */
export const getTextSize = (text: string, font: string) => {
  const height = parseInt(font, 10);
  // default width of half height in case context doesn't exist
  let width = (text.length * height) / 2;

  const element = window.document.createElement("canvas");
  const context = element.getContext("2d");
  if (context) {
    context.font = font;
    width = context.measureText(text).width;
  }

  const size: { width: number; height: number } = {
    height,
    width,
  };
  return size;
};

/**
 * Returns either a string formatted for units at the end of a label/value, or an empty string
 * @param units
 */
export function appendUnits(units: string | undefined) {
  return units ? ` (${units})` : "";
}

/**
 * Joins an array of strings and adds a count if > 1
 * e.x. (#) item1 | item2...
 */
export function joinItemsWithCount(values: string[]) {
  return values.length <= 1
    ? values.join(" | ")
    : `(${values.length}) ${values.join(" | ")}`;
}
